import React, { useState, useEffect } from 'react';
import type { OrganiserEntry, ContactInfoEntry } from 'simple-creator/types';
import { EventLocationForm, EventLocationData } from './EventLocationForm';
import { ContactInfoForm } from './ContactInfoForm';

const createEmptyEventLocation = (): EventLocationData => ({
  name: '',
  address: '',
  coordinates: { lat: 0, lng: 0 },
});

interface OrganizerSectionProps {
  organisers: OrganiserEntry[];
  onChange: (organisers: OrganiserEntry[]) => void;
  onValidationChange?: (isValid: boolean) => void;
  className?: string;
}

export const OrganizerSection: React.FC<OrganizerSectionProps> = ({
  organisers,
  onChange,
  onValidationChange,
  className = '',
}) => {
  const organiser = organisers[0] || { name: '' };
  
  const [showLocation, setShowLocation] = useState(
    () => Boolean(organiser.location && organiser.location.length > 0)
  );
  const [showContact, setShowContact] = useState(
    () => Boolean(organiser.contactInfos && organiser.contactInfos.length > 0)
  );

  useEffect(() => {
    if (organiser.location && organiser.location.length > 0) {
      setShowLocation(true);
    }
    if (organiser.contactInfos && organiser.contactInfos.length > 0) {
      setShowContact(true);
    }
  }, [organiser.location, organiser.contactInfos]);

  const updateOrganiser = (updates: Partial<OrganiserEntry>) => {
    const updated = { ...organiser, ...updates };
    const hasContent = updated.name || 
      updated.salutation || 
      (updated.location && updated.location.length > 0) ||
      (updated.contactInfos && updated.contactInfos.length > 0);
    
    if (hasContent) {
      onChange([updated]);
    } else {
      onChange([]);
    }
  };

  const handleNameChange = (name: string) => {
    updateOrganiser({ name });
  };

  const handleSalutationChange = (salutation: string) => {
    updateOrganiser({ salutation: salutation || undefined });
  };

  const handleAddLocation = () => {
    setShowLocation(true);
    const emptyLoc = createEmptyEventLocation();
    updateOrganiser({ 
      location: [{
        name: emptyLoc.name,
        address: {
          street: emptyLoc.address,
          zip: '',
          locality: '',
          country: '',
          region: '',
        },
        coordinates: emptyLoc.coordinates,
      }] 
    });
  };

  const handleRemoveLocation = () => {
    setShowLocation(false);
    updateOrganiser({ location: undefined });
  };

  const handleLocationChange = (locationData: EventLocationData) => {
    updateOrganiser({
      location: [{
        name: locationData.name,
        address: {
          street: locationData.address,
          zip: '',
          locality: '',
          country: '',
          region: '',
        },
        coordinates: locationData.coordinates,
      }],
    });
  };

  const handleAddContact = () => {
    setShowContact(true);
    updateOrganiser({ contactInfos: [] });
  };

  const handleRemoveContact = () => {
    setShowContact(false);
    updateOrganiser({ contactInfos: undefined });
  };

  const handleContactChange = (contacts: ContactInfoEntry[]) => {
    updateOrganiser({ contactInfos: contacts.length > 0 ? contacts : undefined });
  };

  const currentLocation: EventLocationData = organiser.location?.[0] 
    ? {
        name: organiser.location[0].name,
        address: organiser.location[0].address?.street || '',
        coordinates: organiser.location[0].coordinates || { lat: 0, lng: 0 },
      }
    : createEmptyEventLocation();

  return (
    <div className={className}>
      <label className="label block mb-3 text-base font-semibold text-gray-900">
        Organizer <span className="text-gray-400 font-normal">(optional)</span>
      </label>

      <div className="space-y-4">
        {/* Salutation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Salutation
          </label>
          <input
            type="text"
            value={organiser.salutation || ''}
            onChange={(e) => handleSalutationChange(e.target.value)}
            placeholder="e.g., Mr., Ms., Dr."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-balloon/20 focus:border-balloon"
          />
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name
          </label>
          <input
            type="text"
            value={organiser.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Organization or person name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-balloon/20 focus:border-balloon"
          />
          <p className="text-xs text-gray-500 mt-1">
            The name of the person or organization hosting this event
          </p>
        </div>

        {/* Location Section */}
        {showLocation ? (
          <div className="border border-gray-200 rounded-xl p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Organizer Location</span>
              <button
                type="button"
                onClick={handleRemoveLocation}
                className="text-gray-400 hover:text-red-500 text-xs flex items-center gap-1 transition-colors"
                aria-label="Remove organizer location"
              >
                <span>✕</span> Remove
              </button>
            </div>
            <EventLocationForm
              value={currentLocation}
              onChange={handleLocationChange}
              showVenueName={true}
              venueNamePlaceholder="Location name"
              addressPlaceholder="Start typing an address..."
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={handleAddLocation}
            className="w-full py-2.5 text-balloon border-2 border-dashed border-balloon/50 hover:border-balloon hover:bg-balloon/5 rounded-xl transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            <span>+</span>
            <span>Add organizer location</span>
          </button>
        )}

        {/* Contact Section */}
        {showContact ? (
          <div className="border border-gray-200 rounded-xl p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Organizer Contact</span>
              <button
                type="button"
                onClick={handleRemoveContact}
                className="text-gray-400 hover:text-red-500 text-xs flex items-center gap-1 transition-colors"
                aria-label="Remove organizer contact"
              >
                <span>✕</span> Remove
              </button>
            </div>
            <ContactInfoForm
              contacts={organiser.contactInfos || []}
              onChange={handleContactChange}
              onValidationChange={onValidationChange}
              coreTypes={['phone', 'email', 'website']}
              extendedTypes={[]}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={handleAddContact}
            className="w-full py-2.5 text-balloon border-2 border-dashed border-balloon/50 hover:border-balloon hover:bg-balloon/5 rounded-xl transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            <span>+</span>
            <span>Add organizer contact</span>
          </button>
        )}
      </div>
    </div>
  );
};
